import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { WeeklyResultByStoreReportService } from './weekly-result-by-store-report.service';
import { ShopinfoService } from '../../system/shopinfo/shopinfo.service';
import { ShopListItem } from '../../system/shopinfo/model/shopinfo.model';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-weekly-result-by-store-report',
  standalone: false,
  templateUrl: './weekly-result-by-store-report.component.html',
  styleUrl: './weekly-result-by-store-report.component.scss'
})
export class WeeklyResultByStoreReportComponent {

  title: string = "Weekly Result By Store";
  IsDownloading: boolean = false;
  editForm: FormGroup;
  currentYear: number;
  selectedYear: any;
  weeks: any[] = [];
  shopListData: any = [];
  financialYears: any[] = [];
  years: number[] = [];
  curFinYear!: string;

  // --- Added for drop/reset functionality ---
  isExpandedShop = false;
  resetButtonEnabledForShop: boolean = false;
  showSelectedShopOnly: boolean = false;
  shopFilter: string = '';
  permission: any;
  selectedShopType: 'shop' | 'dt' = 'shop';

  onShopTypeChange(value: 'shop' | 'dt'): void {
    this.selectedShopType = value;
    this.editForm.get('shopFilterAll')?.setValue('');
  }

  constructor(private router: Router,
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private weeklyReportService: WeeklyResultByStoreReportService,
    private snackBarService: SnackBarService,
    private ShopinfoService: ShopinfoService,
    private permissionService: PermissionService,
    public dialog: MatDialog,) {

    let uomId = this.route.snapshot.paramMap.get('id');
    this.editForm = this.builder.group({
      year: this.builder.control(null),
      week_no: this.builder.control([]),
      description: this.builder.control([]),
      decimal_places: this.builder.control([]),
      uom_symbol: this.builder.control([]),
      compound_unit: this.builder.control([]),
      base_uom_id: this.builder.control([]),
      sort_by: this.builder.control('shop_name'),
      shopFilter: this.builder.control(''),
      sort_order: this.builder.control('asc'),
      shopFilterAll: this.builder.control(''),
      shopFilterSelected: this.builder.control('')
    });

    this.currentYear = new Date().getFullYear();

    this.editForm.valueChanges.subscribe(() => {
      this.saveToSessionStorage();
    });
    // Listen for shopFilter changes
    this.editForm.get('shopFilter')?.valueChanges.subscribe((value: string) => {
      this.shopFilter = value;
    });


    this.permission = permissionService.getPagePermission("weekly-result-by-store")
    console.log(this.permission);
  }

  getCurrentWeek(weeks: any[]): any {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return weeks.find(week => {
      const startDate = new Date(week.start_date);
      const endDate = new Date(week.end_date);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return today >= startDate && today <= endDate;
    });
  }

  sortOptions = [
    { value: 'shop_name', label: 'Shop Name' },
    { value: 'this_year_this_week_sales', label: 'This Year Sales' },
    { value: 'last_year_this_week_sales', label: 'Last Year Sales' },
    { value: 'this_week_cust_orders', label: 'This Year Customer Orders' },
    { value: 'last_year_cus_order', label: 'Last Year Customer Orders' },
    { value: 'this_week_labour_cost', label: 'This Year Labour Cost' },
    { value: 'last_year_this_week_labour_cost', label: 'Last Year Labour Cost' },
  ];

  sortOrderOptions = [
    { value: 'asc', label: 'Ascending' },
    { value: 'desc', label: 'Descending' }
  ];

  saveToSessionStorage(): void {
    const selectedShopIds = this.shopListData
      .filter((shop: any) => {
        if (!shop.selected) return false;
        const isDt = shop.item.name && shop.item.name.toUpperCase().includes(' DT');
        return this.selectedShopType === 'dt' ? isDt : !isDt;
      })
      .map((shop: any) => shop.item.id);

    const shopIds = selectedShopIds.length > 0 ? selectedShopIds : [];

    const reportData = {
      year: this.editForm.get('year')?.value,
      weekNo: this.editForm.get('week_no')?.value,
      shopId: shopIds,
      sortBy: this.editForm.get('sort_by')?.value,
      sortDir: this.editForm.get('sort_order')?.value,
      isExport: 0
    };

    sessionStorage.setItem('weeklyStoreReportData', JSON.stringify(reportData));
  }

  loadFromSessionStorage(): void {
    const savedData = sessionStorage.getItem('weeklyStoreReportData');
    if (savedData) {
      const reportData = JSON.parse(savedData);

      this.editForm.patchValue({
        year: reportData.year,
        week_no: reportData.weekNo,
        sort_by: reportData.sortBy,
        sort_order: reportData.sortDir
      }, { emitEvent: false });

      if (reportData.shopId && reportData.shopId.length > 0) {
        this.shopListData.forEach((shop: any) => {
          shop.selected = reportData.shopId.includes(shop.item.id);
        });
      } else {
        this.shopListData.forEach((shop: any) => shop.selected = false);
      }

      if (reportData.year) {
        this.onYearChange();
      }
    }
    this.updateResetButtonStateForShop();
  }

  ngOnInit(): void {
    this.loadData();
    this.shopList();
  }

  onYearChange(): void {
    this.selectedYear = this.editForm.get('year')?.value;
    const selectedFinYear = this.financialYears.find(fy => fy.year === this.selectedYear);

    if (selectedFinYear) {
      let filteredWeeks = selectedFinYear.weeks;

      // Filter weeks to only show those that have started
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      filteredWeeks = selectedFinYear.weeks.filter((week: any) => {
        const weekStartDate = new Date(week.start_date);
        weekStartDate.setHours(0, 0, 0, 0);
        return weekStartDate <= today;
      });

      // Sort weeks in descending order by week number
      this.weeks = filteredWeeks
        .map((week: any) => ({
          ...week,
          displayText: `${this.padWeekNumber(week.week_no)} (${week.start_date} - ${week.end_date})`
        }))
        .sort((a: any, b: any) => b.week_no - a.week_no);

      if (this.weeks.length > 0) {
        this.editForm.get('week_no')?.setValue(this.weeks[0].week_no);
      }
    } else {
      this.weeks = [];
      this.editForm.get('week_no')?.setValue(null);
    }
  }

  buildRequestData(): any {
    return {
      search: {
        filters: {
          simpleSearch: [],
          advSearch: [],
          scope: {
            limit: "100",
            offset: "0"
          }
        },
        sort: [
          {
            column: "wd.year",
            order: "desc"
          }
        ]
      }
    };
  }

  loadData(): void {
    let requestData = this.buildRequestData();
    this.weeklyReportService.getWeekNoList(requestData).subscribe(response => {
      if (response.status === 'SUCCESS') {
        // Filter financial years to show only those that have actually started
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        this.financialYears = response.data.finYears
          .filter((fy: any) => {
            // If the financial year has weeks, check if the first week has started
            if (fy.weeks && fy.weeks.length > 0) {
              // Sort weeks to get the first week by week number
              const sortedWeeks = fy.weeks.sort((a: any, b: any) => a.week_no - b.week_no);
              const firstWeek = sortedWeeks[0];
              const firstWeekStartDate = new Date(firstWeek.start_date);
              firstWeekStartDate.setHours(0, 0, 0, 0);

              // Only show financial years whose first week has started
              return firstWeekStartDate <= today;
            }
            // If no weeks data, fall back to year comparison
            return fy.year <= today.getFullYear();
          })
          .sort((a: any, b: any) => b.year - a.year);
        this.years = this.financialYears.map(fy => fy.year);

        if (!this.editForm.get('year')?.value) {
          this.curFinYear = response.data.curFinYear;
          if (this.curFinYear) {
            const yearValue = parseInt(this.curFinYear);
            this.editForm.get('year')?.setValue(yearValue, { emitEvent: false });

            const currentFinYear = this.financialYears.find(fy => fy.year === yearValue);
            if (currentFinYear) {
              // Filter weeks to only show those that have started
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const filteredWeeks = currentFinYear.weeks.filter((week: any) => {
                const weekStartDate = new Date(week.start_date);
                weekStartDate.setHours(0, 0, 0, 0);
                return weekStartDate <= today;
              });

              // Sort weeks in descending order by week number
              this.weeks = filteredWeeks
                .map((week: any) => ({
                  ...week,
                  displayText: `${this.padWeekNumber(week.week_no)} (${week.start_date} - ${week.end_date})`
                }))
                .sort((a: any, b: any) => b.week_no - a.week_no);

              const currentWeek = this.getCurrentWeek(this.weeks);
              if (currentWeek) {
                this.editForm.get('week_no')?.setValue(currentWeek.week_no);
              } else if (this.weeks.length > 0) {
                this.editForm.get('week_no')?.setValue(this.weeks[0].week_no);
              }
            }
          }
        }
      }
    });
  }

  padWeekNumber(weekNo: number): string {
    return weekNo.toString().padStart(2, '0');
  }

  shopList(): void {
    let requestData = { search: { filters: { filter: [], scope: { offset: 0, limit: 1000000 } }, sort: [{ column: "shop.name", order: "ASC" }] } }

    this.ShopinfoService.getShopList(requestData).subscribe(
      (response: any) => {
        this.shopListData = [];
        var index = 0;
        response.data.shops.forEach((item: any) => {
          if (item.name) {
            var data = { index: index, item: item, selected: false };
            this.shopListData.push(data);
            index++;
          }
        });
        this.updateResetButtonStateForShop(); // <-- update reset button state after loading
      },
      (error: any) => { },
    );
  }

  // --- Drop/Reset Functionality ---

  getSelectedCount(sourceList: any[]): number {
    return sourceList?.filter((item: any) => item.selected).length || 0;
  }

  updateResetButtonStateForShop(): void {
    this.resetButtonEnabledForShop = this.shopListData?.some((shop: any) => shop.selected);
  }

  onShopSelectionChange(): void {
    this.updateResetButtonStateForShop();
    this.saveToSessionStorage();
  }

  resetShopSelection(): void {
    this.shopListData.forEach((shop: any) => shop.selected = false);
    this.updateResetButtonStateForShop();
    this.saveToSessionStorage();
  }

  // --- End Drop/Reset Functionality ---

  // Filter logic for shop list (like applyFilter in your dialog)
  applyShopFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.editForm.get('shopFilter')?.setValue(filterValue);
  }

  get filteredShopListData() {
    let filteredList = this.shopListData;
    if (this.selectedShopType === 'dt') {
      filteredList = filteredList.filter((item: any) => item.item.name && item.item.name.toUpperCase().includes(' DT'));
    } else {
      filteredList = filteredList.filter((item: any) => !item.item.name || !item.item.name.toUpperCase().includes(' DT'));
    }
    if (this.showSelectedShopOnly) {
      filteredList = filteredList.filter((item: any) => item.selected);
    }
    const filter = this.editForm.get('shopFilterAll')?.value?.toLowerCase() || '';
    if (filter) {
      filteredList = filteredList.filter((item: { item: { name: string; }; }) =>
        item.item.name.toLowerCase().includes(filter)
      );
    }
    return filteredList;
  }

  toggleSelectAllShop() {
    const currentTypeShops = this.shopListData.filter((item: any) => {
      const isDt = item.item.name && item.item.name.toUpperCase().includes(' DT');
      return this.selectedShopType === 'dt' ? isDt : !isDt;
    });
    const allSelected = currentTypeShops.every((item: any) => item.selected);
    currentTypeShops.forEach((item: any) => (item.selected = !allSelected));
    this.updateResetButtonStateForShop();
    this.saveToSessionStorage();
  }

  getSelectAllIconShop(): string {
    const currentTypeShops = this.shopListData.filter((item: any) => {
      const isDt = item.item.name && item.item.name.toUpperCase().includes(' DT');
      return this.selectedShopType === 'dt' ? isDt : !isDt;
    });
    const allSelected = currentTypeShops.every((item: any) => item.selected);
    const someSelected = currentTypeShops.some((item: any) => item.selected);

    if (allSelected) {
      return 'check_box';
    }
    if (someSelected) {
      return 'indeterminate_check_box';
    }
    return 'check_box_outline_blank';
  }

  get selectedShopSourcesCount(): number {
    return this.shopListData?.filter((item: any) => {
      if (!item.selected) return false;
      const isDt = item.item.name && item.item.name.toUpperCase().includes(' DT');
      return this.selectedShopType === 'dt' ? isDt : !isDt;
    })?.length || 0;
  }

  navigateToWeeklyReport() {
    this.router.navigate(['/report/weekly-result-by-store-view']);
  }

  exportClicked(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      let snackBarData: SnackBarData = {
        message: 'Please fill all required fields.',
        title: 'Validation Error',
        type: 'error'
      };
      this.snackBarService.openSnackBar(snackBarData);
      return;
    }

    this.IsDownloading = true;

    const selectedShopIds = this.shopListData
      .filter((shop: any) => {
        if (!shop.selected) return false;
        const isDt = shop.item.name && shop.item.name.toUpperCase().includes(' DT');
        return this.selectedShopType === 'dt' ? isDt : !isDt;
      })
      .map((shop: any) => shop.item.id);

    const shopIds = selectedShopIds.length > 0 ? selectedShopIds : [];

    const reportData = {
      year: this.editForm.get('year')?.value,
      weekNo: this.editForm.get('week_no')?.value,
      shopId: shopIds,
      sortBy: this.editForm.get('sort_by')?.value,
      sortDir: this.editForm.get('sort_order')?.value,
      isExport: 1
    };

    if (reportData) {
      this.weeklyReportService.getWeekNoExportList(reportData).subscribe({
        next: (response: Blob) => {
          const reader = new FileReader();

          reader.onload = () => {
            try {
              const text = reader.result as string;
              const json = JSON.parse(text);

              // Check for no data found status
              if (json.status === 'NO_DATA_FOUND') {
                let snackBarData: SnackBarData = {
                  message: 'No data found.',
                  title: 'No Data Found!',
                  type: 'warn'
                }
                this.IsDownloading = false;
                this.snackBarService.openSnackBar(snackBarData);
                return;
              }

              // Check for other error statuses
              if (json.status === 'ERROR' || json.status === 'FAILED') {
                let snackBarData: SnackBarData = {
                  message: json.message || 'An error occurred while generating the report.',
                  title: 'Error!',
                  type: 'error'
                }
                this.IsDownloading = false;
                this.snackBarService.openSnackBar(snackBarData);
                return;
              }

              // If we reach here with a JSON response, show warning
              let snackBarData: SnackBarData = {
                message: 'Unexpected response format from server.',
                title: 'Warning!',
                type: 'warn'
              }
              this.IsDownloading = false;
              this.snackBarService.openSnackBar(snackBarData);

            } catch (e) {
              // If JSON parsing fails, it's likely a binary file (Excel)
              // Check if the response is empty or very small
              if (response.size === 0) {
                let snackBarData: SnackBarData = {
                  message: 'No data available for export.',
                  title: 'No Data Found!',
                  type: 'warn'
                }
                this.IsDownloading = false;
                this.snackBarService.openSnackBar(snackBarData);
                return;
              }

              // Check if response is too small to be a valid file
              if (response.size < 100) {
                let snackBarData: SnackBarData = {
                  message: 'No data found for the selected criteria.',
                  title: 'No Data Found!',
                  type: 'warn'
                }
                this.IsDownloading = false;
                this.snackBarService.openSnackBar(snackBarData);
                return;
              }

              // Valid binary file - proceed with download
              const blob = new Blob([response], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'weekly-report-by-store.xlsx';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              this.IsDownloading = false;
            }
          };

          reader.readAsText(response);
        },
        error: (error) => {
          console.error('Error fetching data:', error);
          this.IsDownloading = false;
        }
      });
    }
  }



}